import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Route, Sparkles } from 'lucide-react';

interface TourPromptProps {
  firstName: string;
  onStartTour: () => void;
  onSkipTour: () => void;
}

export function TourPrompt({ firstName, onStartTour, onSkipTour }: TourPromptProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkipTour();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSkipTour]);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-prompt-title"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none"
    >
      {/* Background Effects — must not capture clicks meant for the card */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-cyan/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
          }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyber-purple/30 rounded-full blur-3xl"
        />
      </div>

      {/* Main Content */}
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="relative z-10 mx-4 max-w-lg pointer-events-auto"
      >
        {/* Glow Effect */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-cyber-cyan/20 via-cyber-blue/20 to-cyber-purple/20 blur-2xl" />
        
        {/* Card */}
        <div className="relative bg-dark-800/90 backdrop-blur-xl border border-cyber-cyan/30 rounded-2xl p-8 shadow-2xl">
          {/* Holographic Border */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-1 bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple opacity-20 blur-sm"
            />
          </div>

          <div className="relative z-10 space-y-6">
            {/* Header */}
            <div className="text-center space-y-3">
              {/* COMO Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.6 }}
                className="flex justify-center"
              >
                <div className="relative">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.4, 0.7, 0.4],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                    className="absolute inset-0 bg-cyber-cyan/30 rounded-full blur-xl"
                  />
                  <div className="relative w-16 h-16 bg-gradient-to-br from-cyber-cyan to-cyber-blue rounded-full flex items-center justify-center">
                    <Route className="w-8 h-8 text-white" />
                  </div>
                </div>
              </motion.div>

              {/* Title */}
              <div>
                <h2 id="tour-prompt-title" className="text-2xl font-bold text-white mb-2">
                  Would you like a quick tour?
                </h2>
                <p className="text-gray-400">
                  I am COMO, your CoreMonit AI Operator. Let me show you around your monitoring universe.
                </p>
              </div>
            </div>

            {/* Tour Features */}
            <div className="space-y-3">
              <p className="text-sm text-gray-300 font-medium">This tour will cover:</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Dashboard overview',
                  'Monitoring system',
                  'Logs and analytics',
                  'Infrastructure monitoring',
                  'AI insights',
                  'Alert management',
                  'Team collaboration',
                  'Settings & integrations'
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    className="flex items-center gap-2 text-sm"
                  >
                    <div className="w-1.5 h-1.5 bg-cyber-cyan rounded-full" />
                    <span className="text-gray-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Estimated Time */}
            <div className="bg-dark-700/50 border border-cyber-cyan/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-cyber-cyan" />
                  <span className="text-sm text-gray-300">Estimated time:</span>
                </div>
                <span className="text-sm font-medium text-cyber-cyan">~3 minutes</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onStartTour}
                className="flex-1 bg-gradient-to-r from-cyber-cyan to-cyber-blue text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 transition-opacity shadow-lg shadow-cyber-cyan/20"
              >
                Yes, start tour
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onSkipTour}
                className="flex-1 bg-dark-700/50 border border-gray-600 text-gray-300 py-3 px-6 rounded-xl font-medium hover:bg-dark-600/50 transition-colors"
              >
                No, skip tour
              </motion.button>
            </div>

            {/* Skip Note */}
            <p className="text-xs text-gray-500 text-center">
              Press <kbd className="rounded border border-gray-600 px-1">Esc</kbd> to skip. You can start the tour later from
              Help → Start Platform Tour.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}